/**
 * TokenPurchaseSuccess
 *
 * Page displayed after successful token purchase.
 * Shows success message with confetti celebration and redirects to main app.
 */

import { useEffect, useState } from 'react';
import { CheckCircle, ArrowRight, Gift, Wallet } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { useTranslation } from '../../hooks/useTranslation';
import { buildCloudApiUrl } from '../../services/urlResolver';
import { publicAsset } from '../../utils/assetUrl';

// Confetti celebration function (lightweight, no external dependency)
function launchConfetti() {
  const colors = ['#22c55e', '#3b82f6', '#8b5cf6', '#f59e0b', '#ec4899'];
  const confettiCount = 150;

  for (let i = 0; i < confettiCount; i++) {
    const confetti = document.createElement('div');
    confetti.style.cssText = `
      position: fixed;
      width: ${Math.random() * 10 + 5}px;
      height: ${Math.random() * 10 + 5}px;
      background: ${colors[Math.floor(Math.random() * colors.length)]};
      left: ${Math.random() * 100}vw;
      top: -20px;
      opacity: ${Math.random() + 0.5};
      border-radius: ${Math.random() > 0.5 ? '50%' : '0'};
      pointer-events: none;
      z-index: 9999;
      animation: confetti-fall ${Math.random() * 3 + 2}s linear forwards;
    `;
    document.body.appendChild(confetti);

    // Remove confetti after animation
    setTimeout(() => confetti.remove(), 5000);
  }

  // Add keyframes if not already added
  if (!document.getElementById('confetti-style')) {
    const style = document.createElement('style');
    style.id = 'confetti-style';
    style.textContent = `
      @keyframes confetti-fall {
        0% {
          transform: translateY(0) rotate(0deg);
          opacity: 1;
        }
        100% {
          transform: translateY(100vh) rotate(720deg);
          opacity: 0;
        }
      }
    `;
    document.head.appendChild(style);
  }
}

interface TokenPurchaseSuccessProps {
  onNavigateHome: () => void;
}

export function TokenPurchaseSuccess({ onNavigateHome }: TokenPurchaseSuccessProps) {
  const { t } = useTranslation();
  const { user, getIdToken } = useAuth();
  const [balance, setBalance] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);

  // /api/v1/tokens/* is Cloud-Run-only — use buildCloudApiUrl so the post-purchase
  // balance refresh works in Electron desktop too (getApiBaseUrl -> localhost -> 404).

  // Launch confetti on mount
  useEffect(() => {
    launchConfetti();
  }, []);

  // Fetch updated balance after purchase
  useEffect(() => {
    const fetchBalance = async () => {
      if (!user) {
        setLoading(false);
        return;
      }

      try {
        const token = await getIdToken();
        if (!token) {
          setLoading(false);
          return;
        }

        const response = await fetch(buildCloudApiUrl('/api/v1/tokens/balance'), {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (response.ok) {
          const data = await response.json();
          setBalance(data.data.balance);
        }
      } catch (err) {
        console.error('Failed to fetch balance:', err);
      } finally {
        setLoading(false);
      }
    };

    // Small delay to allow webhook to process
    const timer = setTimeout(fetchBalance, 1000);
    return () => clearTimeout(timer);
  }, [user, getIdToken]);

  // Auto-redirect after 10 seconds
  useEffect(() => {
    const timer = setTimeout(() => {
      onNavigateHome();
    }, 10000);

    return () => clearTimeout(timer);
  }, [onNavigateHome]);

  // Debug log
  console.log(
    '[TokenPurchaseSuccess] Rendering, user:',
    user?.email,
    'balance:',
    balance,
    'loading:',
    loading
  );

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-b from-green-50 to-white dark:from-secondary-900 dark:to-secondary-800">
      <div className="text-center max-w-md bg-white dark:bg-secondary-800 p-8 rounded-2xl shadow-xl border border-secondary-200 dark:border-secondary-700">
        {/* Success icon */}
        <div className="relative mb-6">
          <div className="absolute inset-0 bg-green-500/20 rounded-full blur-xl animate-pulse" />
          <div className="inline-flex items-center justify-center w-20 h-20 bg-green-100 dark:bg-green-900/30 rounded-full">
            <CheckCircle className="h-12 w-12 text-green-500" />
          </div>
        </div>

        {/* Title */}
        <h1 className="text-2xl sm:text-3xl font-bold text-secondary-900 dark:text-white mb-3">
          {t('pricing.success.title')}
        </h1>

        {/* Message */}
        <p className="text-secondary-600 dark:text-secondary-400 mb-4">
          {t('pricing.success.subtitle')}
        </p>

        {/* Crypto confirmation banner */}
        <div className="p-4 bg-purple-50 dark:bg-purple-900/20 rounded-xl mb-6 border border-purple-200 dark:border-purple-800">
          <div className="flex items-center justify-center gap-2 text-purple-700 dark:text-purple-300">
            <Wallet className="h-5 w-5" />
            <span className="text-sm font-medium">
              {t('pricing.success.cryptoConfirmed') || 'Blockchain transaction verified'}
            </span>
          </div>
        </div>

        {/* Balance card */}
        {!loading && balance !== null && (
          <div className="bg-secondary-50 dark:bg-secondary-700 rounded-xl p-4 mb-6 shadow-sm border border-secondary-200 dark:border-secondary-600">
            <div className="flex items-center justify-center gap-3">
              <img
                src={publicAsset('token-coin-64.png')}
                alt=""
                className="h-8 w-8"
                draggable={false}
              />
              <div>
                <p className="text-sm text-secondary-500 dark:text-secondary-400">
                  {t('pricing.success.newBalance')}
                </p>
                <p className="text-2xl font-bold text-secondary-900 dark:text-white">
                  {balance.toLocaleString()} {t('common.tokens')}{' '}
                  <img
                    src={publicAsset('token-coin-32.png')}
                    alt=""
                    className="h-5 w-5 inline"
                    draggable={false}
                  />
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Action buttons */}
        <div className="space-y-3">
          <button
            onClick={onNavigateHome}
            className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 bg-primary-500 hover:bg-primary-600 text-white rounded-xl font-semibold transition-colors min-h-[44px] shadow-lg shadow-primary-500/25"
          >
            {t('pricing.success.startCreating')}
            <ArrowRight className="h-5 w-5" />
          </button>

          <button
            onClick={() => {
              window.history.pushState({}, '', '/pricing');
              window.location.reload();
            }}
            className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 border border-secondary-300 dark:border-secondary-600 text-secondary-700 dark:text-secondary-300 font-medium rounded-xl hover:bg-secondary-50 dark:hover:bg-secondary-700 transition-colors min-h-[44px]"
          >
            <Gift className="h-5 w-5" />
            {t('pricing.success.buyMore')}
          </button>
        </div>

        {/* Auto-redirect notice */}
        <p className="text-xs text-secondary-500 dark:text-secondary-400 mt-4">
          {t('pricing.success.autoRedirect')}
        </p>
      </div>
    </div>
  );
}
